import { useCallback, useContext } from 'react';
import { stageContext } from '@/context/stages/context';
import { StagesActionTypes } from '@/context/stages/reducer';
import { Route } from '@/services/routes/route.vm';
import { Stage } from '@/services/stages/stage.vm';

const useRouteStages = () => {
  const { stages, dispatch } = useContext(stageContext);

  const loadRouteStages = useCallback(
    (routeId: Route['id'], stages: Stage[] = []) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      dispatch<{ routeId: Route['id']; stages: Stage[] }>({
        type: StagesActionTypes.LOAD_ROUTE_STAGES,
        payload: { routeId, stages },
      });
    },
    [dispatch],
  );

  const loadStage = useCallback(
    (routeId: Route['id'], stageId: Stage['id'], stage: Stage) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      dispatch<{ routeId: Route['id']; stageId: Stage['id']; stage: Stage }>({
        type: StagesActionTypes.LOAD_STAGE,
        payload: { routeId, stageId, stage },
      });
    },
    [dispatch],
  );

  const getRouteStages = useCallback(
    (routeId: Route['id']): Stage[] => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      return stages?.[routeId]?.stages ?? [];
    },
    [stages],
  );

  const getStage = useCallback(
    (routeId: Route['id'], stageId: Stage['id']): Stage | undefined => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      return stages[routeId].stages?.find((stage) => stage.id === stageId);
    },
    [stages],
  );

  const addStage = useCallback(
    (routeId: Route['id'], stage: Stage) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      dispatch<{ routeId: Route['id']; stage: Stage }>({
        type: StagesActionTypes.ADD_STAGE,
        payload: { routeId, stage },
      });
    },
    [dispatch],
  );

  const deleteStage = useCallback(
    (routeId: Route['id'], stageId: Stage['id']) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      dispatch<{ routeId: Route['id']; stageId: Stage['id'] }>({
        type: StagesActionTypes.DELETE_STAGE,
        payload: { routeId, stageId },
      });
    },
    [dispatch],
  );

  const updateStage = useCallback(
    (routeId: Route['id'], stage: Partial<Stage>) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      dispatch<{ routeId: Route['id']; stage: Partial<Stage> }>({
        type: StagesActionTypes.UPDATE_STAGE,
        payload: { routeId, stage },
      });
    },
    [dispatch],
  );

  const routeStagesHasLoaded = useCallback(
    (routeId: Route['id']): boolean => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      return stages?.[routeId]?.hasLoaded ?? false;
    },
    [stages],
  );

  return {
    getStage,
    addStage,
    loadStage,
    updateStage,
    deleteStage,
    getRouteStages,
    loadRouteStages,
    routeStagesHasLoaded,
  };
};

export default useRouteStages;
